import type { ColorItem } from '@octopus-do/octopus-project-core'
import { ProjectActions } from '@octopus-do/octopus-project-core'
import { put } from 'redux-saga/effects'

import type { XmlImportModel } from '@/utils/xml'
import { normalizeHexColor } from '@/utils/xml'

const UNTITLED_COLOR_TITLE = 'Untitled'

// Цвета палитры нового проекта, которых в файле нет вообще, из легенды убираем:
// иначе легенда карты состоит из чужих цветов, а объявленные в файле в ней не
// видны. Использованный, но не объявленный цвет на карте что-то значит — он остаётся.
function* hideUnusedLegendColors({
  projectColors,
  fileHexCodes,
}: {
  projectColors: ColorItem[]
  fileHexCodes: Set<string>
}): Generator<any, void, any> {
  for (const color of projectColors) {
    const hexCode = normalizeHexColor(color.hexCode)

    if (!color.showInLegend || (hexCode && fileHexCodes.has(hexCode))) {
      continue
    }

    yield put(
      ProjectActions.colors.updateShowInLegend({
        id: color.id,
        showInLegend: false,
      })
    )
  }
}

export function* applyColors({
  model,
  projectColors,
  projectColorByHex,
  colorIdMap,
}: {
  model: XmlImportModel
  projectColors: ColorItem[]
  projectColorByHex: Map<string, string>
  colorIdMap: Map<string, string>
}): Generator<any, void, any> {
  const hasDeclaredColors = model.colors.some((color) => color.declared)

  if (hasDeclaredColors) {
    yield* hideUnusedLegendColors({
      projectColors,
      fileHexCodes: new Set(model.colors.map((color) => color.hexCode)),
    })
  }

  const colorsToAdd: ColorItem[] = []

  for (const color of model.colors) {
    const existingColorId = projectColorByHex.get(color.hexCode)

    if (existingColorId) {
      colorIdMap.set(color.id, existingColorId)

      // Имя из файла главнее названия из палитры: импорт всегда создаёт новый
      // проект, так что переименовывать нечего терять.
      if (color.title) {
        yield put(
          ProjectActions.colors.updateTitle({
            id: existingColorId,
            title: color.title,
          })
        )
      }

      if (color.showInLegend !== undefined) {
        yield put(
          ProjectActions.colors.updateShowInLegend({
            id: existingColorId,
            showInLegend: color.showInLegend,
          })
        )
      }

      continue
    }

    colorsToAdd.push({
      id: color.id,
      hexCode: color.hexCode,
      title: color.title || UNTITLED_COLOR_TITLE,
      showInLegend: color.showInLegend ?? false,
    })
    projectColorByHex.set(color.hexCode, color.id)
    colorIdMap.set(color.id, color.id)
  }

  if (colorsToAdd.length === 0) {
    return
  }

  yield put(
    ProjectActions.colors.add({
      items: colorsToAdd,
    })
  )
}
